<template>
  <div
    :class="[
      'flex flex-shrink-0 items-center justify-center overflow-hidden font-semibold font-be-vietnam',
      sizeClasses,
      variantClasses,
      containerClass,
    ]"
    aria-hidden
  >
    <img
      v-if="src && !imageError"
      :src="src"
      :alt="name"
      class="h-full w-full object-cover"
      @error="imageError = true"
    />
    <span
      v-else
      class="inline-flex size-full select-none items-center justify-center leading-none"
    >
      {{ initials }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

export type AvatarSize = 'sm' | 'md' | 'lg';
export type AvatarVariant = 'default' | 'light';

/** Status do colaborador: define a cor de fundo do avatar (ex.: listagem). */
export type AvatarStatus = 'invited' | 'in_review' | 'approved' | 'rejected' | 'inactive';

export interface EcxAvatarProps {
  /** Nome da pessoa (usado para iniciais: primeiro + último nome) */
  name: string;
  /** URL da imagem (opcional; se ausente ou falhar, exibe iniciais) */
  src?: string | null;
  /** Tamanho do avatar */
  size?: AvatarSize;
  /** Variante de cor: default = bg royal-950 + texto branco; light = bg royal-950 + texto royal-100 */
  variant?: AvatarVariant;
  /** Status (ex.: colaborador): aplica cor por status; tem precedência sobre variant quando definido */
  status?: AvatarStatus | null;
  /** Classes opcionais no container (override total de cor quando passada) */
  containerClass?: string;
}

const props = withDefaults(defineProps<EcxAvatarProps>(), {
  src: undefined,
  size: 'md',
  variant: 'default',
  status: undefined,
  containerClass: '',
});

/** Cor de fundo do avatar por status (texto sempre branco). */
function getStatusClasses(status: AvatarStatus): string {
  switch (status) {
    case 'invited':
      return 'bg-royal-950 text-white';
    case 'approved':
    case 'rejected':
      return 'bg-royal-800 text-white';
    case 'in_review':
      return 'bg-royal-500 text-white';
    case 'inactive':
      return 'bg-grey-600 text-white';
    default:
      return 'bg-royal-800 text-white';
  }
}

const imageError = ref(false);

watch(() => props.src, () => {
  imageError.value = false;
});

const initials = computed(() => {
  const name = (props.name ?? '').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const firstLetter = parts[0][0];
    const lastLetter = parts[parts.length - 1][0];
    return `${firstLetter}${lastLetter}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase() || '—';
});

const sizeClasses = computed(() => {
  const map: Record<AvatarSize, string> = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
  };
  return map[props.size];
});

const variantClasses = computed(() => {
  const rounded = 'rounded-xl';
  if (props.containerClass?.trim()) {
    return '';
  }
  if (props.status) {
    return `${rounded} ${getStatusClasses(props.status)}`;
  }
  const map: Record<AvatarVariant, string> = {
    default: `${rounded} bg-royal-950 text-white`,
    light: `${rounded} bg-royal-950 text-royal-100`,
  };
  return map[props.variant];
});
</script>
